import { useState, useEffect } from 'react'
import { X, Image } from 'lucide-react'
import { useCreateRelease, useUpdateRelease } from '@/hooks/use-music'
import MediaPicker from '@/components/media/media-picker'
import type { Tables } from '@/types/supabase'

type MusicRelease = Tables<'music_releases'>

const RELEASE_TYPE_LABELS: Record<string, string> = {
  single: 'Single',
  album: 'Album',
  ep: 'EP',
}

const STREAMING_PLATFORMS = [
  { key: 'spotify', label: 'Spotify' },
  { key: 'apple_music', label: 'Apple Music' },
  { key: 'youtube', label: 'YouTube' },
  { key: 'youtube_music', label: 'YouTube Music' },
]

interface FormState {
  title: string
  release_type: string
  release_date: string
  description: string
  is_published: boolean
  streaming: Record<string, string>
}

const EMPTY_FORM: FormState = {
  title: '',
  release_type: 'single',
  release_date: '',
  description: '',
  is_published: false,
  streaming: {},
}

interface ReleaseFormModalProps {
  open: boolean
  onClose: () => void
  release?: MusicRelease
}

export default function ReleaseFormModal({ open, onClose, release }: ReleaseFormModalProps) {
  const isEdit = !!release
  const createRelease = useCreateRelease()
  const updateRelease = useUpdateRelease()

  const [form, setForm] = useState<FormState>(EMPTY_FORM)
  const [coverUrl, setCoverUrl] = useState<string | null>(null)
  const [coverAssetId, setCoverAssetId] = useState<string | null>(null)
  const [mediaPickerOpen, setMediaPickerOpen] = useState(false)

  useEffect(() => {
    if (open) {
      if (release) {
        const links = (release.streaming_links as Record<string, string> | null) ?? {}
        setForm({
          title: release.title,
          release_type: release.release_type,
          release_date: release.release_date ?? '',
          description: release.description ?? '',
          is_published: release.is_published,
          streaming: links,
        })
        setCoverUrl(release.cover_url ?? null)
        setCoverAssetId((release as unknown as { asset_id?: string | null }).asset_id ?? null)
      } else {
        setForm(EMPTY_FORM)
        setCoverUrl(null)
        setCoverAssetId(null)
      }
      createRelease.reset()
      updateRelease.reset()
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, release?.id])

  const isPending = createRelease.isPending || updateRelease.isPending
  const mutationError = createRelease.error ?? updateRelease.error

  function set<K extends keyof FormState>(key: K, value: FormState[K]) {
    setForm((f) => ({ ...f, [key]: value }))
  }

  function setStreaming(platform: string, url: string) {
    setForm((f) => ({
      ...f,
      streaming: url.trim() ? { ...f.streaming, [platform]: url } : Object.fromEntries(
        Object.entries(f.streaming).filter(([k]) => k !== platform)
      ),
    }))
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()

    const payload = {
      title: form.title.trim(),
      release_type: form.release_type,
      release_date: form.release_date || null,
      description: form.description.trim() || null,
      cover_url: coverUrl,
      asset_id: coverAssetId,
      is_published: form.is_published,
      streaming_links: form.streaming,
    }

    if (isEdit && release) {
      updateRelease.mutate({ id: release.id, ...payload }, { onSuccess: onClose })
    } else {
      createRelease.mutate(payload, { onSuccess: onClose })
    }
  }

  if (!open) return null

  return (
    <>
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />

      <div className="relative z-10 w-full max-w-lg rounded-xl border border-border bg-card shadow-xl">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-border">
          <h2 className="font-semibold">{isEdit ? 'Edit Release' : 'Tambah Release'}</h2>
          <button
            type="button"
            onClick={onClose}
            className="rounded p-1 text-muted-foreground hover:bg-accent hover:text-accent-foreground"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="px-6 py-5 space-y-4 max-h-[75vh] overflow-y-auto">
          {/* Cover image */}
          <div className="space-y-2">
            <p className="text-sm font-medium">Cover Art</p>
            {coverUrl ? (
              <div className="relative w-full h-36 rounded-lg overflow-hidden bg-muted">
                <img src={coverUrl} alt="cover preview" className="h-full w-full object-cover" />
                <button
                  type="button"
                  onClick={() => { setCoverUrl(null); setCoverAssetId(null) }}
                  className="absolute top-2 right-2 rounded-full bg-black/60 p-1 text-white hover:bg-black/80"
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              </div>
            ) : (
              <button
                type="button"
                disabled={mediaPickerOpen}
                onClick={() => setMediaPickerOpen(true)}
                className="flex w-full items-center justify-center gap-2 rounded-lg border border-dashed border-border h-24 text-sm text-muted-foreground hover:bg-accent hover:text-accent-foreground transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Image className="h-4 w-4" />
                Pilih dari Media
              </button>
            )}
            {coverUrl && (
              <button
                type="button"
                disabled={mediaPickerOpen}
                onClick={() => setMediaPickerOpen(true)}
                className="inline-flex items-center gap-2 rounded-md border border-border px-3 py-1.5 text-sm hover:bg-accent disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Image className="h-3.5 w-3.5" />
                Ganti Cover
              </button>
            )}
          </div>

          {/* Title */}
          <div className="space-y-1">
            <label className="text-sm font-medium">Judul *</label>
            <input
              required
              value={form.title}
              onChange={(e) => set('title', e.target.value)}
              className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm focus:border-primary focus:outline-none"
              placeholder="Nama album / single"
            />
          </div>

          {/* Release type + date */}
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <label className="text-sm font-medium">Tipe *</label>
              <select
                required
                value={form.release_type}
                onChange={(e) => set('release_type', e.target.value)}
                className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm focus:border-primary focus:outline-none"
              >
                {Object.entries(RELEASE_TYPE_LABELS).map(([val, label]) => (
                  <option key={val} value={val}>{label}</option>
                ))}
              </select>
            </div>
            <div className="space-y-1">
              <label className="text-sm font-medium">Tanggal Rilis</label>
              <input
                type="date"
                value={form.release_date}
                onChange={(e) => set('release_date', e.target.value)}
                className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm focus:border-primary focus:outline-none"
              />
            </div>
          </div>

          {/* Description */}
          <div className="space-y-1">
            <label className="text-sm font-medium">Deskripsi</label>
            <textarea
              rows={2}
              value={form.description}
              onChange={(e) => set('description', e.target.value)}
              className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm focus:border-primary focus:outline-none resize-none"
              placeholder="Deskripsi singkat (opsional)"
            />
          </div>

          {/* Streaming links */}
          <fieldset className="rounded-lg border border-border p-4 space-y-3">
            <legend className="text-sm font-medium px-1">Link Streaming</legend>
            {STREAMING_PLATFORMS.map((p) => (
              <div key={p.key} className="space-y-1">
                <label className="text-xs text-muted-foreground">{p.label}</label>
                <input
                  type="url"
                  value={form.streaming[p.key] ?? ''}
                  onChange={(e) => setStreaming(p.key, e.target.value)}
                  className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm focus:border-primary focus:outline-none"
                  placeholder={`https://...`}
                />
              </div>
            ))}
          </fieldset>

          {/* Publish toggle */}
          <label className="flex items-center gap-2 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={form.is_published}
              onChange={(e) => set('is_published', e.target.checked)}
              className="rounded border-border accent-primary"
            />
            <span className="text-sm">Publikasikan langsung</span>
          </label>

          {/* Error */}
          {mutationError && (
            <p className="text-sm text-destructive">
              {(mutationError as Error).message ?? 'Terjadi kesalahan. Coba lagi.'}
            </p>
          )}

          {/* Footer actions */}
          <div className="flex justify-end gap-2 pt-2 border-t border-border">
            <button
              type="button"
              onClick={onClose}
              className="rounded-md border border-border px-4 py-2 text-sm hover:bg-accent"
            >
              Batal
            </button>
            <button
              type="submit"
              disabled={isPending}
              className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
            >
              {isPending ? 'Menyimpan...' : isEdit ? 'Simpan Perubahan' : 'Tambah Release'}
            </button>
          </div>
        </form>
      </div>
    </div>

    <MediaPicker
      open={mediaPickerOpen}
      onClose={() => setMediaPickerOpen(false)}
      onSelect={(assetId, url) => {
        setCoverAssetId(assetId)
        setCoverUrl(url)
        setMediaPickerOpen(false)
      }}
    />
    </>
  )
}
